import { useState } from 'react';
import styles from './IGStoryUploadCell.module.css';
import { BookingsApiModel, BookingsTableData } from '../helpers';
import { CellProps } from 'react-table';
import { showNotification } from '../../../../utils/showNotification';
import cn from 'classnames';
import { ReactComponent as CopyIcon } from '../../../../assets/Copy.svg';
import Tooltip from '../../../../ui/Tooltip/Tooltip';
import copyToClipboard from 'copy-to-clipboard';
import { ReactComponent as PlusIcon } from '../../../../assets/PlusIcon.svg';
import { ReactComponent as EditIcon } from '../../../../assets/Edit.svg';
import { ReactComponent as EyeIcon } from '../../../../assets/Eye.svg';
import Modal from '../../../../ui-new/Modal/Modal';
import Button from '../../../../ui-new/Button/Button';
import PreviewEvidenceFile from './PreviewEvidenceFile/PreviewEvidenceFile';

type Props<T extends BookingsTableData> = CellProps<T> & {
    updateBooking?: (bookingId: number, data: BookingsApiModel) => Promise<void>;
};

const IGStoryUploadCell = <T extends BookingsTableData>({ updateBooking, row: { original }, value }: Props<T>) => {
    const [isUpdating, setIsUpdating] = useState(false);
    const bookingID = original.id;
    const [buttonRef, setButtonRef] = useState<HTMLButtonElement | null>(null);
    const uploadLabel = value ? (
        <>
            Update IG Story file&nbsp;
            <EditIcon className={styles.editIcon} />
        </>
    ) : (
        <>
            Upload IG Story file&nbsp;
            <PlusIcon className={styles.addIcon} />
        </>
    );

    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [previewButtonRef, setPreviewButtonRef] = useState<HTMLButtonElement | null>(null);

    return (
        <>
            <div className={styles.container}>
                <label htmlFor={`story-file-${bookingID}`}>
                    <div className={styles.uploadButton}>{isUpdating ? 'Uploading...' : uploadLabel}</div>
                    <input
                        type="file"
                        id={`story-file-${bookingID}`}
                        accept="video/*, image/*"
                        className={cn(styles.hiddenInput, { [styles.diabled]: isUpdating })}
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;

                            setIsUpdating(true);

                            updateBooking?.(bookingID, { instagram_story_evidence_file: file })
                                .then(() => showNotification('IG Story file uploaded successfully', 'info'))
                                .catch(() => showNotification('Failed to upload IG Story file', 'error'))
                                .finally(() => setIsUpdating(false));
                        }}
                    />
                </label>

                {value && (
                    <>
                        <button
                            ref={setButtonRef}
                            className={styles.actionButton}
                            onClick={() => {
                                const success = copyToClipboard(value);
                                if (success) {
                                    showNotification('Post url copied!', 'info');
                                }
                            }}
                        >
                            <CopyIcon className={styles.actionButtonIcon} />
                        </button>
                        <Tooltip anchorElement={buttonRef} value="copy post url" />

                        <button
                            ref={setPreviewButtonRef}
                            className={styles.actionButton}
                            onClick={() => setIsPreviewModalOpen(true)}
                        >
                            <EyeIcon className={styles.actionButtonIcon} />
                        </button>
                        <Tooltip anchorElement={previewButtonRef} value="preview file" />
                    </>
                )}
            </div>

            <Modal
                className={styles.previewModal}
                closeOnEscape
                closeOnOverlayClick
                isOpen={isPreviewModalOpen}
                onClose={() => setIsPreviewModalOpen(false)}
            >
                <Modal.Header>
                    Preview {original.id} - {original.influencer_name} - {original.client_name} -{' '}
                    {original.campaign_name}
                </Modal.Header>

                <PreviewEvidenceFile className={styles.preview} url={value} />

                <Modal.Actions>
                    <Button appearance="ghost" onClick={() => setIsPreviewModalOpen(false)}>
                        Close
                    </Button>
                </Modal.Actions>
            </Modal>
        </>
    );
};

export default IGStoryUploadCell;
